import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where
} from "firebase/firestore";
import { getAuth, sendPasswordResetEmail } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, functions as firebaseFunctions } from "../firebase";
import { useConfirm } from "../components/ui/ConfirmProvider";
import { useToast } from "../components/ui/ToastProvider";

const FAMILY_PAGE_SIZE = 25;

const isHelpEntry = (entry) =>
  String(entry?.category || "").toLowerCase() === "admin-help" ||
  String(entry?.view || "").toLowerCase() === "help";

export default function AdminPanel() {
  const { confirm } = useConfirm();
  const { showToast } = useToast();
  const auth = getAuth();
  const setComplimentaryPro = useMemo(
    () => httpsCallable(firebaseFunctions, "setFamilyComplimentaryPro"),
    []
  );
  const setSupportModeration = useMemo(
    () => httpsCallable(firebaseFunctions, "setSupportModeration"),
    []
  );
  const resetFamilyPinsToDefault = useMemo(
    () => httpsCallable(firebaseFunctions, "resetFamilyPinsToDefault"),
    []
  );
  const appendSupportThreadMessage = useMemo(
    () => httpsCallable(firebaseFunctions, "appendSupportThreadMessage"),
    []
  );

  const [entries, setEntries] = useState([]);
  const [families, setFamilies] = useState([]);
  const [feedbackFilter, setFeedbackFilter] = useState("open");
  const [helpFilter, setHelpFilter] = useState("open");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [feedbackFamilyNames, setFeedbackFamilyNames] = useState({});
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastSaved, setBroadcastSaved] = useState(false);
  const [adminHelpAlertEmails, setAdminHelpAlertEmails] = useState("");
  const [adminHelpAlertsEnabled, setAdminHelpAlertsEnabled] = useState(true);
  const [supportSaved, setSupportSaved] = useState(false);
  const [billingStatus, setBillingStatus] = useState("");
  const [supportStatus, setSupportStatus] = useState("");
  const [familySearchInput, setFamilySearchInput] = useState("");
  const [familySearch, setFamilySearch] = useState("");
  const [familyLoading, setFamilyLoading] = useState(false);
  const [familyPage, setFamilyPage] = useState(1);
  const [familyHasNext, setFamilyHasNext] = useState(false);
  const [familyLastDoc, setFamilyLastDoc] = useState(null);
  const [familyCursorStack, setFamilyCursorStack] = useState([]);
  const [supportModerationByEntry, setSupportModerationByEntry] = useState({});
  const [familyMembersByFamilyId, setFamilyMembersByFamilyId] = useState({});
  const [familyActionBusy, setFamilyActionBusy] = useState({});

  const moderationKey = (familyId, uid) => `${String(familyId || "").trim()}::${String(uid || "").trim()}`;
  const setActionBusy = useCallback((key, busy) => {
    setFamilyActionBusy((prev) => ({ ...prev, [key]: busy }));
  }, []);

  useEffect(() => {
    const q = query(collection(db, "feedback"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const familyIds = Array.from(
      new Set(entries.map((entry) => String(entry.familyId || "")).filter(Boolean))
    );
    if (familyIds.length === 0) {
      setFeedbackFamilyNames({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const resolved = {};
      await Promise.all(
        familyIds.map(async (familyId) => {
          try {
            const snap = await getDoc(doc(db, "families", familyId));
            resolved[familyId] = snap.exists()
              ? snap.data()?.familyName || familyId
              : familyId;
          } catch {
            resolved[familyId] = familyId;
          }
        })
      );
      if (!cancelled) setFeedbackFamilyNames(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [entries]);

  const supportTargets = useMemo(() => {
    const map = new Map();
    entries.forEach((entry) => {
      const familyId = String(entry.familyId || "").trim();
      const userId = String(entry.userId || "").trim();
      if (!familyId || !userId) return;
      const key = moderationKey(familyId, userId);
      if (!map.has(key)) {
        map.set(key, {
          key,
          familyId,
          userId,
          userEmail: entry.userEmail || "",
          userDisplayName: entry.userDisplayName || "",
          familyName: entry.familyName || feedbackFamilyNames[familyId] || familyId
        });
      }
    });
    families.forEach((family) => {
      const familyId = String(family.id || "").trim();
      const ownerUid = String(family.ownerUid || "").trim();
      if (!familyId) return;
      const familyMembers = Array.isArray(familyMembersByFamilyId[familyId])
        ? familyMembersByFamilyId[familyId]
        : [];

      familyMembers.forEach((member) => {
        const memberUid = String(member?.uid || "").trim();
        if (!memberUid) return;
        const key = moderationKey(familyId, memberUid);
        if (map.has(key)) return;
        map.set(key, {
          key,
          familyId,
          userId: memberUid,
          userEmail: member.email || member.userEmail || "",
          userDisplayName: member.displayName || member.name || member.role || "",
          familyName: family.familyName || feedbackFamilyNames[familyId] || familyId
        });
      });

      if (!ownerUid) return;
      const ownerKey = moderationKey(familyId, ownerUid);
      if (!map.has(ownerKey)) {
        map.set(ownerKey, {
          key: ownerKey,
          familyId,
          userId: ownerUid,
          userEmail: family.ownerEmail || "",
          userDisplayName: "Owner",
          familyName: family.familyName || feedbackFamilyNames[familyId] || familyId
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => {
      const familySort = String(a.familyName || "").localeCompare(String(b.familyName || ""));
      if (familySort !== 0) return familySort;
      const aLabel = String(a.userEmail || a.userDisplayName || a.userId);
      const bLabel = String(b.userEmail || b.userDisplayName || b.userId);
      return aLabel.localeCompare(bLabel);
    });
  }, [entries, feedbackFamilyNames, families, familyMembersByFamilyId]);

  const supportTargetsByFamilyId = useMemo(() => {
    const map = {};
    supportTargets.forEach((target) => {
      if (!map[target.familyId]) map[target.familyId] = [];
      map[target.familyId].push(target);
    });
    return map;
  }, [supportTargets]);

  useEffect(() => {
    const pairs = supportTargets.map((item) => item.key);
    if (pairs.length === 0) {
      setSupportModerationByEntry({});
      return;
    }
    let cancelled = false;
    (async () => {
      const resolved = {};
      await Promise.all(
        pairs.map(async (pair) => {
          const [familyId, uid] = pair.split("::");
          try {
            const snap = await getDoc(doc(db, "families", familyId, "supportControl", uid));
            if (snap.exists()) {
              resolved[pair] = snap.data() || {};
            }
          } catch {
            // Ignore failed support control reads in admin panel.
          }
        })
      );
      if (!cancelled) setSupportModerationByEntry(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [supportTargets]);

  useEffect(() => {
    if (!Array.isArray(families) || families.length === 0) {
      setFamilyMembersByFamilyId({});
      return;
    }
    let cancelled = false;
    (async () => {
      const resolved = {};
      await Promise.all(
        families.map(async (family) => {
          const familyId = String(family?.id || "").trim();
          if (!familyId) return;
          try {
            const membersSnap = await getDocs(collection(db, "families", familyId, "members"));
            resolved[familyId] = membersSnap.docs.map((docSnap) => ({
              uid: docSnap.id,
              ...(docSnap.data() || {})
            }));
          } catch {
            resolved[familyId] = [];
          }
        })
      );
      if (!cancelled) setFamilyMembersByFamilyId(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [families]);

  useEffect(() => {
    const ref = doc(db, "appConfig", "global");
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      setBroadcastMessage(data.broadcastMessage || "");
      setAdminHelpAlertEmails(
        Array.isArray(data.adminHelpAlertEmails)
          ? data.adminHelpAlertEmails.join(", ")
          : String(data.adminHelpAlertEmails || data.aiHelpAlertEmails || "")
      );
      setAdminHelpAlertsEnabled(data.adminHelpAlertsEnabled !== false);
    });
    return () => unsub();
  }, []);

  const loadFamiliesPage = useCallback(async ({
    searchTerm = familySearch,
    afterDoc = null,
    page = 1,
    resetStack = false
  } = {}) => {
    setFamilyLoading(true);
    try {
      const normalized = String(searchTerm || "").trim();
      const constraints = [orderBy("familyName", "asc"), limit(FAMILY_PAGE_SIZE + 1)];
      if (normalized) {
        constraints.unshift(
          where("familyName", ">=", normalized),
          where("familyName", "<=", `${normalized}\uf8ff`)
        );
      }
      if (afterDoc) constraints.push(startAfter(afterDoc));

      const snap = await getDocs(query(collection(db, "families"), ...constraints));
      const docs = snap.docs;
      const visibleDocs = docs.slice(0, FAMILY_PAGE_SIZE);
      setFamilies(visibleDocs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      setFamilyHasNext(docs.length > FAMILY_PAGE_SIZE);
      setFamilyLastDoc(visibleDocs.length > 0 ? visibleDocs[visibleDocs.length - 1] : null);
      setFamilyPage(page);
      if (resetStack) setFamilyCursorStack([]);
    } catch (error) {
      setBillingStatus(error?.message || "Unable to load families.");
      setTimeout(() => setBillingStatus(""), 3000);
    } finally {
      setFamilyLoading(false);
    }
  }, [familySearch]);

  useEffect(() => {
    void loadFamiliesPage({
      searchTerm: familySearch,
      afterDoc: null,
      page: 1,
      resetStack: true
    });
  }, [familySearch, loadFamiliesPage]);

  const filterEntriesByStatus = useCallback((items, statusFilter) => {
    if (statusFilter === "all") return items;
    return items.filter((item) => (item.status || "open") === statusFilter);
  }, []);

  const feedbackEntries = useMemo(
    () => entries.filter((entry) => !isHelpEntry(entry)),
    [entries]
  );

  const helpEntries = useMemo(
    () => entries.filter((entry) => isHelpEntry(entry)),
    [entries]
  );

  const visibleFeedbackEntries = useMemo(
    () => filterEntriesByStatus(feedbackEntries, feedbackFilter),
    [feedbackEntries, feedbackFilter, filterEntriesByStatus]
  );

  const visibleHelpEntries = useMemo(
    () => filterEntriesByStatus(helpEntries, helpFilter),
    [helpEntries, helpFilter, filterEntriesByStatus]
  );

  const markResolved = async (entryId, resolved) => {
    await updateDoc(doc(db, "feedback", entryId), {
      status: resolved ? "resolved" : "open",
      resolvedAt: resolved ? new Date() : null
    });
  };

  const sendReply = async (entry) => {
    const message = (replyDrafts[entry.id] || "").trim();
    if (!message) return;
    try {
      await appendSupportThreadMessage({
        feedbackId: entry.id,
        message
      });
      setReplyDrafts((prev) => ({ ...prev, [entry.id]: "" }));
    } catch (error) {
      setSupportStatus(error?.message || "Unable to send reply.");
      setTimeout(() => setSupportStatus(""), 3000);
    }
  };

  const deleteFeedback = async (entryId) => {
    const entry = entries.find((item) => item.id === entryId);
    const confirmDelete = await confirm({
      title: "Delete feedback?",
      message: "Delete this resolved feedback permanently?",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      danger: true
    });
    if (!confirmDelete) return;
    const snapshot = entry ? { ...entry } : null;
    await deleteDoc(doc(db, "feedback", entryId));
    showToast({
      message: "Feedback deleted.",
      actionLabel: snapshot ? "Undo" : null,
      onAction: snapshot
        ? async () => {
            await setDoc(doc(db, "feedback", entryId), snapshot, { merge: true });
          }
        : null
    });
  };

  const saveBroadcast = async () => {
    await setDoc(
      doc(db, "appConfig", "global"),
      {
        broadcastMessage: broadcastMessage.trim(),
        broadcastUpdatedAt: serverTimestamp(),
        broadcastBy: auth.currentUser?.email || null
      },
      { merge: true }
    );
    setBroadcastSaved(true);
    setTimeout(() => setBroadcastSaved(false), 2000);
  };

  const clearBroadcast = async () => {
    setBroadcastMessage("");
    await setDoc(
      doc(db, "appConfig", "global"),
      {
        broadcastMessage: "",
        broadcastUpdatedAt: serverTimestamp(),
        broadcastBy: auth.currentUser?.email || null
      },
      { merge: true }
    );
    setBroadcastSaved(true);
    setTimeout(() => setBroadcastSaved(false), 2000);
  };

  const parseEmails = (raw) =>
    Array.from(
      new Set(
        String(raw || "")
          .split(/[,\n;]+/)
          .map((value) => value.trim().toLowerCase())
          .filter((value) => value.includes("@"))
      )
    );

  const saveSupportAlerts = async () => {
    await setDoc(
      doc(db, "appConfig", "global"),
      {
        adminHelpAlertEmails: parseEmails(adminHelpAlertEmails),
        adminHelpAlertsEnabled,
        adminHelpAlertsUpdatedAt: serverTimestamp(),
        adminHelpAlertsUpdatedBy: auth.currentUser?.email || null
      },
      { merge: true }
    );
    setSupportSaved(true);
    setTimeout(() => setSupportSaved(false), 2000);
  };

  const toggleSupportBlock = async (target, mode) => {
    const familyId = String(target?.familyId || "").trim();
    const uid = String(target?.userId || "").trim();
    if (!familyId || !uid) return;
    const key = moderationKey(familyId, uid);
    const current = supportModerationByEntry[key] || {};
    const field = mode === "adminHelp" ? "adminHelpBlocked" : "feedbackBlocked";
    const active = current?.[field] === true;
    const nextBlocked = !active;

    try {
      const result = await setSupportModeration({
        familyId,
        uid,
        [field]: nextBlocked,
        reason: "Admin moderation from family status panel"
      });
      const moderation = result?.data?.moderation || {};
      setSupportModerationByEntry((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          feedbackBlocked: moderation.feedbackBlocked ?? prev[key]?.feedbackBlocked ?? false,
          adminHelpBlocked: moderation.adminHelpBlocked ?? prev[key]?.adminHelpBlocked ?? false,
          reason: moderation.reason || null
        }
      }));
      setSupportStatus(
        nextBlocked
          ? `${mode === "adminHelp" ? "Admin help" : "Feedback"} disabled for ${target.userEmail || target.userDisplayName || "account"}.`
          : `${mode === "adminHelp" ? "Admin help" : "Feedback"} enabled for ${target.userEmail || target.userDisplayName || "account"}.`
      );
    } catch (error) {
      setSupportStatus(error?.message || "Unable to update support moderation.");
    }
    setTimeout(() => setSupportStatus(""), 3000);
  };

  const toggleComplimentaryPro = async (family) => {
    const next = !family.complimentaryPro;
    const actionKey = `pro:${family.id}`;
    setActionBusy(actionKey, true);
    try {
      await setComplimentaryPro({
        familyId: family.id,
        enabled: next
      });
      setFamilies((prev) =>
        prev.map((item) =>
          item.id === family.id
            ? {
                ...item,
                complimentaryPro: next,
                isPro: next || Boolean(item.paidPro)
              }
            : item
        )
      );
      setBillingStatus(
        next
          ? `Complimentary Pro enabled for ${family.familyName || family.id}.`
          : `Complimentary Pro removed for ${family.familyName || family.id}.`
      );
    } catch (error) {
      setBillingStatus(error?.message || "Unable to update complimentary Pro.");
    } finally {
      setActionBusy(actionKey, false);
    }
    setTimeout(() => setBillingStatus(""), 3000);
  };

  const resetPinsForFamily = async (family) => {
    const actionKey = `pin:${family.id}`;
    const approved = await confirm({
      title: "Reset family PINs?",
      message:
        "This will reset parent and younger child PINs to default (1234). Continue?",
      confirmLabel: "Reset PINs",
      cancelLabel: "Cancel",
      danger: true
    });
    if (!approved) return;
    setActionBusy(actionKey, true);
    try {
      await resetFamilyPinsToDefault({ familyId: family.id });
      setSupportStatus(`PINs reset to default for ${family.familyName || family.id}.`);
    } catch (error) {
      setSupportStatus(error?.message || "Unable to reset family PINs.");
    } finally {
      setActionBusy(actionKey, false);
    }
    setTimeout(() => setSupportStatus(""), 3000);
  };

  const sendAccountPasswordReset = async (target) => {
    const email = String(target?.userEmail || "").trim().toLowerCase();
    if (!email) {
      setSupportStatus("No email available for this account yet.");
      setTimeout(() => setSupportStatus(""), 3000);
      return;
    }
    const actionKey = `password:${target.key}`;
    const approved = await confirm({
      title: "Send password reset?",
      message: `Send a password reset email to ${email}?`,
      confirmLabel: "Send reset",
      cancelLabel: "Cancel"
    });
    if (!approved) return;
    setActionBusy(actionKey, true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSupportStatus(`Password reset email sent to ${email}.`);
    } catch (error) {
      setSupportStatus(error?.message || "Unable to send password reset email.");
    } finally {
      setActionBusy(actionKey, false);
    }
    setTimeout(() => setSupportStatus(""), 3000);
  };

  const applyFamilySearch = () => {
    setFamilySearch(String(familySearchInput || "").trim());
  };

  const resetFamilySearch = () => {
    setFamilySearchInput("");
    setFamilySearch("");
  };

  const goToNextFamiliesPage = async () => {
    if (!familyHasNext || !familyLastDoc) return;
    setFamilyCursorStack((prev) => [...prev, familyLastDoc]);
    await loadFamiliesPage({
      searchTerm: familySearch,
      afterDoc: familyLastDoc,
      page: familyPage + 1
    });
  };

  const goToPreviousFamiliesPage = async () => {
    if (familyPage <= 1) return;
    const nextStack = familyCursorStack.slice(0, -1);
    setFamilyCursorStack(nextStack);
    const previousAfterDoc = nextStack.length > 0 ? nextStack[nextStack.length - 1] : null;
    await loadFamiliesPage({
      searchTerm: familySearch,
      afterDoc: previousAfterDoc,
      page: Math.max(1, familyPage - 1)
    });
  };

  const renderInbox = ({
    inboxEntries,
    inboxTitle,
    emptyMessage,
    statusFilter,
    onStatusFilterChange
  }) => (
    <section className="admin-inbox-section">
      <div className="header-flex">
        <div>
          <h2>{inboxTitle}</h2>
          <p style={{ opacity: 0.7, margin: "6px 0 0" }}>{emptyMessage}</p>
        </div>
        <div className="admin-panel-filters">
          {[
            { id: "open", label: "Open" },
            { id: "resolved", label: "Resolved" },
            { id: "all", label: "All" }
          ].map((option) => (
            <button
              key={option.id}
              className={`btn-nav ${statusFilter === option.id ? "active-settings-tab" : ""}`}
              onClick={() => onStatusFilterChange(option.id)}
            >
              {option.label}
            </button>
          ))}
          <span className="badge">{inboxEntries.length}</span>
        </div>
      </div>
      {inboxEntries.length === 0 ? (
        <p style={{ textAlign: "center", opacity: 0.65, margin: "16px 0 0" }}>No items in this inbox.</p>
      ) : (
        <div className="admin-panel-grid">
          {inboxEntries.map((entry) => (
            <div key={entry.id} className="card admin-feedback-card">
              <div className="admin-feedback-message">
                <strong>{entry.message}</strong>
              </div>
              <div className="admin-feedback-meta">
                {entry.userEmail || entry.userDisplayName || "Email unavailable"} • {entry.familyName || feedbackFamilyNames[entry.familyId] || "Unknown family"} • {entry.platform || "web"}
                {entry.view ? ` • ${entry.view}` : ""}
              </div>
              <div className="admin-feedback-thread">
                {(entry.responses || []).length === 0 ? (
                  <div className="admin-feedback-empty">No replies yet.</div>
                ) : (
                  entry.responses.map((response) => (
                    <div key={response.id || response.createdAt?.seconds || response.message} className="admin-feedback-reply">
                      <div>{response.message}</div>
                      <div className="admin-feedback-reply-meta">
                        {response.authorRole === "admin"
                          ? "Admin"
                          : response.authorName || response.authorEmail || "Family"}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="admin-feedback-reply-box">
                <textarea
                  placeholder="Send a response back to the family..."
                  value={replyDrafts[entry.id] || ""}
                  onChange={(event) =>
                    setReplyDrafts((prev) => ({ ...prev, [entry.id]: event.target.value }))
                  }
                />
                <button className="btn-nav" onClick={() => sendReply(entry)}>
                  Send Reply
                </button>
              </div>
              <div className="admin-feedback-actions">
                {entry.status !== "resolved" ? (
                  <button className="btn-nav success-btn" onClick={() => markResolved(entry.id, true)}>
                    Resolve
                  </button>
                ) : (
                  <>
                    <button className="btn-nav" onClick={() => markResolved(entry.id, false)}>
                      Reopen
                    </button>
                    <button className="btn-nav danger-btn" onClick={() => deleteFeedback(entry.id)}>
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="admin-panel">
      <div className="glass admin-panel-card">
        <div className="header-flex">
          <div>
            <h2>Admin Center</h2>
            <p style={{ opacity: 0.7, margin: "6px 0 0" }}>
              Manage inboxes, admin capabilities, and family status controls.
            </p>
          </div>
          <div className="admin-panel-filters">
            <span className="badge">Feedback: {feedbackEntries.length}</span>
            <span className="badge">Help: {helpEntries.length}</span>
          </div>
        </div>

        <section className="admin-capabilities-section">
          <div className="header-flex">
            <div>
              <h3>Admin Capabilities</h3>
              <p style={{ opacity: 0.7, margin: "6px 0 0" }}>
                Broadcast, admin help alerts, family status, billing, and support controls.
              </p>
            </div>
          </div>

          <div className="admin-broadcast">
            <div>
              <h3>Broadcast Message</h3>
              <p style={{ opacity: 0.7, margin: "6px 0 0" }}>
                This message appears in all apps as a global notice.
              </p>
            </div>
            <textarea
              placeholder="Type a short message to all families..."
              value={broadcastMessage}
              onChange={(event) => setBroadcastMessage(event.target.value)}
            />
            <div className="admin-broadcast-actions">
              <button className="btn-nav" onClick={saveBroadcast}>
                Save broadcast
              </button>
              <button className="btn-nav" onClick={clearBroadcast}>
                Clear broadcast
              </button>
              {broadcastSaved && <span className="success-message">Saved ✓</span>}
            </div>
          </div>

          <div className="admin-broadcast">
            <div>
              <h3>Admin Help Alerts</h3>
              <p style={{ opacity: 0.7, margin: "6px 0 0" }}>
                Send email alerts only when families request human admin help.
              </p>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={adminHelpAlertsEnabled}
                onChange={(event) => setAdminHelpAlertsEnabled(event.target.checked)}
              />
              Enable admin help alert emails
            </label>
            <input
              placeholder="admin@familysync.com, support@familysync.com"
              value={adminHelpAlertEmails}
              onChange={(event) => setAdminHelpAlertEmails(event.target.value)}
            />
            <div className="admin-broadcast-actions">
              <button className="btn-nav" onClick={saveSupportAlerts}>
                Save alerts
              </button>
              {supportSaved && <span className="success-message">Saved ✓</span>}
            </div>
          </div>

          <div className="admin-broadcast">
            <div>
              <h3>Family Status</h3>
              <p style={{ opacity: 0.7, margin: "6px 0 0" }}>
                Manage billing overrides and permanent support controls.
              </p>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <input
                placeholder="Search family name"
                value={familySearchInput}
                onChange={(event) => setFamilySearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") applyFamilySearch();
                }}
              />
              <button className="btn-nav" type="button" onClick={applyFamilySearch}>
                Search
              </button>
              <button className="btn-nav" type="button" onClick={resetFamilySearch}>
                Clear
              </button>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button className="btn-nav" type="button" onClick={goToPreviousFamiliesPage} disabled={familyPage <= 1 || familyLoading}>
                Previous
              </button>
              <small style={{ opacity: 0.8 }}>Page {familyPage}</small>
              <button className="btn-nav" type="button" onClick={goToNextFamiliesPage} disabled={!familyHasNext || familyLoading}>
                Next
              </button>
              {familyLoading && <small style={{ opacity: 0.8 }}>Loading families...</small>}
            </div>

            {billingStatus && <span className="success-message">{billingStatus}</span>}
            {supportStatus && <span className="success-message">{supportStatus}</span>}

            <div className="admin-panel-grid">
              {!familyLoading && families.length === 0 && (
                <p style={{ opacity: 0.7, margin: 0 }}>
                  No families found for this search.
                </p>
              )}
              {families.map((family) => (
                <div key={family.id} className="card admin-feedback-card">
                  <div className="admin-feedback-message">
                    <strong>{family.familyName || "Family"}</strong>
                  </div>
                  <div className="admin-feedback-meta">
                    {family.ownerEmail || family.ownerUid || "Owner email unavailable"}
                  </div>
                  <div className="admin-feedback-meta">
                    {family.isPro ? "PRO" : "FREE"} · complimentary: {family.complimentaryPro ? "on" : "off"}
                  </div>
                  <div className="admin-feedback-actions">
                    <button
                      className="btn-nav admin-mini-btn"
                      onClick={() => toggleComplimentaryPro(family)}
                      disabled={familyActionBusy[`pro:${family.id}`]}
                    >
                      {familyActionBusy[`pro:${family.id}`]
                        ? "Saving..."
                        : family.complimentaryPro
                        ? "Disable complimentary Pro"
                        : "Enable complimentary Pro"}
                    </button>
                    <button
                      className="btn-nav admin-mini-btn"
                      type="button"
                      onClick={() => resetPinsForFamily(family)}
                      disabled={familyActionBusy[`pin:${family.id}`]}
                    >
                      {familyActionBusy[`pin:${family.id}`] ? "Resetting..." : "Reset PINs to default"}
                    </button>
                  </div>
                  <div className="admin-feedback-meta">
                    Support controls
                  </div>
                  <div className="admin-support-targets">
                    {(supportTargetsByFamilyId[family.id] || []).map((target) => {
                      const key = target.key;
                      const moderation = supportModerationByEntry[key] || {};
                      const feedbackBlocked = moderation?.feedbackBlocked === true;
                      const adminHelpBlocked = moderation?.adminHelpBlocked === true;
                      return (
                        <div key={key} className="admin-support-target">
                          <div className="admin-feedback-meta" style={{ opacity: 0.95 }}>
                            <strong>{target.userEmail || target.userDisplayName || "Account"}</strong>
                          </div>
                          <div className="admin-feedback-meta">
                            Feedback: {feedbackBlocked ? "disabled" : "enabled"} • Admin help: {adminHelpBlocked ? "disabled" : "enabled"}
                          </div>
                          <div className="admin-feedback-actions admin-support-actions">
                            <button className="btn-nav admin-mini-btn" type="button" onClick={() => toggleSupportBlock(target, "feedback")}>
                              {feedbackBlocked ? "Enable feedback" : "Disable feedback"}
                            </button>
                            <button className="btn-nav admin-mini-btn" type="button" onClick={() => toggleSupportBlock(target, "adminHelp")}>
                              {adminHelpBlocked ? "Enable admin help" : "Disable admin help"}
                            </button>
                            <button
                              className="btn-nav admin-mini-btn"
                              type="button"
                              onClick={() => sendAccountPasswordReset(target)}
                              disabled={
                                !String(target.userEmail || "").trim() ||
                                familyActionBusy[`password:${target.key}`]
                              }
                              title={
                                String(target.userEmail || "").trim()
                                  ? `Send reset to ${target.userEmail}`
                                  : "No email found for this account"
                              }
                            >
                              {familyActionBusy[`password:${target.key}`]
                                ? "Sending..."
                                : "Send password reset"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {(supportTargetsByFamilyId[family.id] || []).length === 0 && (
                      <p style={{ opacity: 0.7, margin: 0 }}>No support accounts found for this family yet.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {renderInbox({
          inboxEntries: visibleFeedbackEntries,
          inboxTitle: "Feedback Inbox",
          emptyMessage: "Feedback messages from families.",
          statusFilter: feedbackFilter,
          onStatusFilterChange: setFeedbackFilter
        })}

        {renderInbox({
          inboxEntries: visibleHelpEntries,
          inboxTitle: "Help Inbox",
          emptyMessage: "Admin help requests from families.",
          statusFilter: helpFilter,
          onStatusFilterChange: setHelpFilter
        })}
      </div>
    </div>
  );
}
